
import React from "react";
import { BannerRoot, BannerIcon, BannerIconWrapper, BannerText } from "./styles.infoLabel";

const DEFAULT_ICON_SRC = "/logos/exclamation-line.svg";

/**
 * InfoBanner — generic icon + message strip.
 *
 * @param {string|React.ReactElement} icon         - URL string (renders <img>) or React element (rendered directly).
 *                                                   Defaults to /logos/exclamation-line.svg.
 * @param {number}                   iconSize      - Width & height for <img> icons in px. Default: 20.
 * @param {string}                   $bg           - CSS background-color value.
 * @param {string}                   $border       - CSS border shorthand.
 * @param {string}                   $borderRadius - CSS border-radius.
 * @param {string}                   $padding      - CSS padding shorthand.
 * @param {string}                   $gap          - CSS gap between icon and text.
 * @param {string}                   $color        - CSS text color.
 * @param {string}                   $fontSize     - CSS font-size for the message.
 * @param {string}                   $fontWeight   - CSS font-weight.
 * @param {string}                   $lineHeight   - CSS line-height.
 * @param {React.ReactNode}          children      - Plain text or JSX children.
 * @param {string}                   className     - Optional extra class for the root element.
 * @param {object}                   style         - Optional inline styles merged onto the root element.
 */
export default function InfoBanner({
  // icon
  icon,
  iconSize = 20,
  iconStyle,
  // appearance (transient props forwarded to BannerRoot)
  $bg,
  $border,
  $borderRadius,
  $padding,
  $gap,
  $color,
  $fontSize,
  $fontWeight,
  $lineHeight,
  // content
  children,
  // passthrough
  className,
  style,
}) {
  // Resolve whether the caller passed a path string, a React element, or nothing (use default).
  const resolvedIcon =
    icon === undefined
      ? <BannerIcon src={DEFAULT_ICON_SRC} $size={`${iconSize}px`} alt="" aria-hidden="true" style={iconStyle} />
      : typeof icon === "string"
        ? <BannerIcon src={icon} $size={`${iconSize}px`} alt="" aria-hidden="true" style={iconStyle} />
        : icon; // React element (e.g. <Info />, pre-built <img …/> from caller)

  const content = <BannerText>{children}</BannerText>;

  return (
    <BannerRoot
      $bg={$bg}
      $border={$border}
      $borderRadius={$borderRadius}
      $padding={$padding}
      $gap={$gap}
      $color={$color}
      $fontSize={$fontSize}
      $fontWeight={$fontWeight}
      $lineHeight={$lineHeight}
      className={className}
      style={style}
    >
      <BannerIconWrapper>{resolvedIcon}</BannerIconWrapper>
      {content}
    </BannerRoot>
  );
}
